import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

// Bulk Clone calls the Kamino backend directly, as the signed-in user: the same routes the
// Kamino Bootstrap form uses, so no bootstrap token and no Armory backend route.

/** A stack Kamino is configured to clone into, e.g. Demo or Develop. */
export type KaminoEnvironment = { id: string; name: string };

export type CloneUser = {
  email: string;
  groups: string[];
  /** A source-company user this one takes over (24-char hex id). */
  replacedUserId?: string;
};

/** The body of `POST /api/kamino/clone-company-and-workflows-v2`. */
export type CloneRequest = {
  environmentId: string;
  companyId: string;
  name: string;
  displayName: string;
  newUsers: CloneUser[];
  sendEmailInvitations: boolean;
  placeholderFileUrl: string;
  salesforceInfo: { accountId: string; accountName: string };
};

/**
 * What came of sending one row. There is no idempotency key, so the distinction matters:
 * - `submitted`: Kamino started the clone. Never resend it.
 * - `failed`: nothing was created. Safe to fix and resend.
 * - `unknown`: a company may exist. Check the Kamino Operations page before resending.
 */
export type SubmitOutcome =
  | { status: 'submitted'; operationId: string }
  | { status: 'failed' | 'unknown'; error: string };

/** Kamino's own status for a submitted clone. */
export type CloneStatus =
  | 'pending'
  | 'in_progress'
  | 'export_ready'
  | 'completed'
  | 'failed';

export type CloneProgress = {
  status: CloneStatus;
  /** Set once Kamino reports the new company; poll on `status`, not on this. */
  clonedCompanyId?: string;
  error?: string;
};

export interface BulkCloneApi {
  getEnvironments(): Promise<KaminoEnvironment[]>;
  /** Never throws: every failure is reported as a `failed` or `unknown` outcome. */
  submitClone(request: CloneRequest): Promise<SubmitOutcome>;
  getCloneProgress(operationId: string): Promise<CloneProgress>;
}

export const bulkCloneApiRef = createApiRef<BulkCloneApi>({
  id: 'plugin.armory.bulk-clone',
});

/** Kamino's operation page for a submitted clone, inside this Backstage app. */
export function trackingPath(operationId: string): string {
  return `/kamino/operations/${encodeURIComponent(operationId)}`;
}

const CHECK_KAMINO_HINT =
  'The clone may or may not have started — check the Kamino Operations page before resending ' +
  'this row, or you may create a duplicate company.';

export class BulkCloneClient implements BulkCloneApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getEnvironments(): Promise<KaminoEnvironment[]> {
    const res = await this.fetchApi.fetch(`${await this.baseUrl()}/environments`);
    if (!res.ok) {
      throw new Error(`Couldn't load Kamino's environments (HTTP ${res.status}).`);
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data)) throw new Error("Kamino's environment list was unreadable.");
    return data
      .filter(e => typeof e?.id === 'string' && e.id)
      .map(e => ({ id: e.id, name: typeof e.name === 'string' && e.name ? e.name : e.id }));
  }

  async submitClone(request: CloneRequest): Promise<SubmitOutcome> {
    let res: Response;
    try {
      res = await this.fetchApi.fetch(
        `${await this.baseUrl()}/clone-company-and-workflows-v2`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
          // A redirect is a sign-in or proxy wall in front of Kamino, not Kamino itself.
          redirect: 'manual',
        },
      );
    } catch (e) {
      // Not `instanceof Error`: fetch's TypeError can come from another realm.
      const message = (e as { message?: unknown } | null)?.message;
      const reason = typeof message === 'string' ? message : String(e);
      return unknown(`Clone request did not complete (${reason}). ${CHECK_KAMINO_HINT}`);
    }

    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400)) {
      return {
        status: 'failed',
        error:
          'Clone request was redirected — your Backstage sign-in may have expired. Reload the page ' +
          'and resend this row. No clone was started.',
      };
    }
    const body = await res.text().catch(() => '');
    if (res.ok) {
      const operationId = parseOperationId(body);
      return operationId
        ? { status: 'submitted', operationId }
        : unknown(
            `Clone API ${res.status} but no operationId in response: ${truncate(body)}. ${CHECK_KAMINO_HINT}`,
          );
    }
    if (res.status < 500) {
      return { status: 'failed', error: `Clone API ${res.status}: ${formatApiError(body)}` };
    }
    // Kamino answers 500 for every upstream failure, including one after the clone started.
    return unknown(`Clone API ${res.status}: ${formatApiError(body)}. ${CHECK_KAMINO_HINT}`);
  }

  async getCloneProgress(operationId: string): Promise<CloneProgress> {
    const res = await this.fetchApi.fetch(
      `${await this.baseUrl()}/workflow/${encodeURIComponent(operationId)}`,
    );
    if (!res.ok) {
      throw new Error(
        `Couldn't check the clone's status (HTTP ${res.status}): ${formatApiError(await res.text().catch(() => ''))}`,
      );
    }
    const state = await res.json();
    if (typeof state?.status !== 'string') {
      throw new Error("Couldn't check the clone's status: the response was unreadable.");
    }
    const clonedCompanyId = state.result?.clonedCompanyId;
    return {
      status: state.status,
      ...(typeof clonedCompanyId === 'string' && clonedCompanyId ? { clonedCompanyId } : {}),
      ...(typeof state.error === 'string' && state.error ? { error: state.error } : {}),
    };
  }

  private baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('kamino');
  }
}

function unknown(error: string): SubmitOutcome {
  return { status: 'unknown', error };
}

function parseOperationId(body: string): string {
  try {
    const id = JSON.parse(body)?.operationId;
    return typeof id === 'string' ? id : '';
  } catch {
    return '';
  }
}

/**
 * Flattens Kamino's two error shapes: Backstage's `{"error":{"name","message"}}` (schema and
 * auth errors) and the service's `{"success":false,"error","errorCode"}`.
 */
export function formatApiError(body: string): string {
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch {
    return truncate(body);
  }
  if (!parsed || typeof parsed !== 'object') return truncate(body);
  const err = parsed.error;
  if (err && typeof err === 'object' && err.message) {
    return truncate(err.name ? `${err.name}: ${err.message}` : String(err.message));
  }
  if (typeof err === 'string' && err) {
    return truncate(parsed.errorCode ? `${err} (${parsed.errorCode})` : err);
  }
  if (parsed.message) return truncate(String(parsed.message));
  return truncate(body);
}

function truncate(text: string): string {
  return text.length > 500 ? `${text.slice(0, 500)}…` : text;
}
